/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { FileCheck2, LockKeyhole, RefreshCw, Upload } from 'lucide-react';
import { chamarApi } from '@/nucleo/auth/cliente-api';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
const NS = '/bff/v1/j08-s03-conciliar-referencia-selar';
export function ConciliarReferenciaPage() {
  const [s, setS] = useState<any>(),
    [busy, setBusy] = useState(false),
    [arquivo, setArquivo] = useState<File | null>(null),
    [resolucoes, setResolucoes] = useState<Record<string, string>>({});
  async function call(path: string, body?: any) {
    setBusy(true);
    try {
      const e = await chamarApi<any>(NS + path, {
        metodo: path === '/state' ? 'GET' : 'POST',
        corpo: body,
      });
      setS(e.state);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    void call('/state');
  }, []);
  if (!s)
    return (
      <main role="status" className="p-6">
        Carregando conciliação…
      </main>
    );
  const c = s.competencias[0];
  async function importar() {
    if (!arquivo || !c) return;
    const conteudo = await arquivo.text();
    await call('/verbs/importar_arquivo', {
      atesteId: c.id,
      nomeArquivo: arquivo.name,
      conteudo,
      chaveIdempotencia: crypto.randomUUID(),
    });
  }
  return (
    <main className="min-w-0 max-w-[calc(100vw-2rem)] space-y-5 overflow-hidden">
      <header>
        <h1 className="text-3xl font-semibold">
          <FileCheck2 className="mr-2 inline h-7 w-7" />
          {s.titulo}
        </h1>
        <p>{s.descricao}</p>
        <p className="text-sm text-muted-foreground">
          Sessão: {s.sessao?.nome} · {s.sessao?.email}
        </p>
      </header>
      {s.alertas.map((a: any, i: number) => (
        <div role="alert" className="rounded-md border border-destructive p-3" key={i}>
          {a.mensagem} <span className="font-mono text-xs">{a.codigo}</span>
        </div>
      ))}
      {!c ? (
        <p className="rounded-md border border-dashed p-6">
          Nenhuma composição com dupla alçada aguarda conciliação.
        </p>
      ) : (
        <article className="space-y-4 rounded-md border p-4">
          <h2 className="font-semibold">
            {c.protocolo} · composição v{c.composicaoVersao}
          </h2>
          <p className="break-all font-mono text-xs">Hash da composição {c.composicaoHash}</p>
          {!c.selo && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="grid gap-1 text-sm" htmlFor="arquivo-referencia">
                Arquivo oficial
                <Input
                  id="arquivo-referencia"
                  type="file"
                  accept=".json,.csv,.txt"
                  onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                />
              </label>
              <Button disabled={busy || !arquivo} onClick={() => void importar()}>
                <Upload className="mr-2 h-4 w-4" />
                Importar arquivo
              </Button>
            </div>
          )}
          {c.referencias.map((r: any) => (
            <section key={r.id} className="space-y-3 rounded-md bg-muted p-3">
              <h3 className="font-medium">
                {r.nomeArquivo} · {r.estado}
              </h3>
              <p className="break-all font-mono text-xs">SHA-256 {r.hashArquivo}</p>
              {r.vendorLab && (
                <p className="text-sm">
                  Fonte controlada: {r.fornecedor} · operação {r.operacaoId} · correlação{' '}
                  {r.correlacaoId}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {r.estado === 'IMPORTADO' && (
                  <Button
                    disabled={busy}
                    onClick={() => void call('/verbs/iniciar_conciliacao', { referenciaId: r.id })}
                  >
                    Iniciar conciliação
                  </Button>
                )}
                {r.estado === 'PENDENTE' && (
                  <Button
                    disabled={busy}
                    onClick={() =>
                      void call('/verbs/reconciliar_referencia', { referenciaId: r.id })
                    }
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Atualizar fonte
                  </Button>
                )}
                {r.estado === 'CONCILIADO' && !c.selo && (
                  <Button
                    disabled={busy}
                    onClick={() =>
                      void call('/verbs/selar_competencia', {
                        referenciaId: r.id,
                        chaveIdempotencia: crypto.randomUUID(),
                      })
                    }
                  >
                    <LockKeyhole className="mr-2 h-4 w-4" />
                    Selar competência
                  </Button>
                )}
              </div>
              {r.divergencias.map((d: any) => (
                <div key={d.id} className="rounded-md border p-3">
                  <p>
                    <strong>{d.codigo}</strong> — {d.diagnostico}
                  </p>
                  {d.estado === 'ABERTA' ? (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Input
                        aria-label={`Resolução ${d.codigo}`}
                        value={resolucoes[d.id] ?? ''}
                        onChange={(e) => setResolucoes({ ...resolucoes, [d.id]: e.target.value })}
                      />
                      <Button
                        variant="outline"
                        disabled={(resolucoes[d.id]?.length ?? 0) < 8}
                        onClick={() =>
                          void call('/verbs/resolver_divergencia', {
                            divergenciaId: d.id,
                            resolucao: resolucoes[d.id],
                            chaveIdempotencia: crypto.randomUUID(),
                          })
                        }
                      >
                        Registrar resolução
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm">
                      Resolvida por {d.resolvidaPor}: {d.resolucao}
                    </p>
                  )}
                </div>
              ))}
            </section>
          ))}
          {c.selo && (
            <section className="rounded-md border border-primary p-4">
              <h3 className="font-semibold">
                <LockKeyhole className="mr-2 inline h-5 w-5" />
                Competência selada
              </h3>
              <p>
                ID {c.selo.id} · por {c.selo.seladoPor} em{' '}
                {new Date(c.selo.seladoEm).toLocaleString('pt-BR')}
              </p>
              <p className="break-all text-xs">
                Composição {c.selo.composicaoHash} · referência {c.selo.referenciaHash}
              </p>
            </section>
          )}
        </article>
      )}
    </main>
  );
}
